import { isArray, mergeWith } from 'lodash-es';
import { Subject } from 'rxjs';
import fetch, { AxiosInstance } from 'axios';
import Core, {
  AppContext,
  IAppContext,
  Lifecycle,
  IBaseConfig,
} from '@byted-cg/linkchat-core';
import { Conversation, im_proto } from '@byted/im-web-sdk';
import dialogHeart from '../lib/heart';
import dialogChange from '../lifecycle/dialogChange';
import Http from '../core/http';
import { getTokenApi } from '../service/apis';
import { DialogCreateResultType } from '../constants';

import { ERROR_TYPE } from '../constants/error';
import { Lang, Region } from '../constants/lang';
import {
  ENV,
  imCloudConfig as ByteConfig,
  logDomain,
  mediaDomain,
} from '../constants/net';

import { DIALOG_STATUS, DIALOG_TYPE, USER_STATUS } from '../constants/status';
import { setMonitorEngine, setLoggerLevel, LoggerLevel } from '../core/metrics';
import userAuthAxios, { EContactType } from '../utils/userAuth/userAuthAxios';
import store from '../store/cache';
import Channel from '../core/ws';
import { Info } from '../constants/metrics';
import { uniqueId, userConfig } from './middleware';
import { createSendFile, createSendMessage } from './sendMessage';
import { onErrorSubject } from './subject';
import {
  ChannelInitProps,
  CombineMessageType,
  EnvConfig,
  ILinkchatInfo,
  ILinkchatProps,
  ImcloudConfig,
  GetSignFnc,
  AppContextType,
  GetUserInfoFunc,
} from './type';
import { CustomSlardarWebMonitor } from '../core/metrics';
import { logger, monitor } from './metrics';

let refreshSign = false;
class Linkchat {
  public messageListChange = new Subject<CombineMessageType[]>();
  public dialogChange = new Subject<Conversation>();
  public appContextChange = new Subject<AppContextType>();
  private _appContext: IAppContext<CombineMessageType, Channel>;
  private _instance?: Core<CombineMessageType, Channel>;
  private appConfig: ILinkchatInfo;
  private imCloudConfig?: ImcloudConfig;
  private getSign: GetSignFnc;
  private getUserInfo: GetUserInfoFunc;
  private onNetError?: (error: any) => void;
  private envConfig: EnvConfig;
  public metrics: any;
  public service?: Http;
  public inNewAuthRole?: boolean;
  static onErrorSubject = onErrorSubject;
  constructor(props: ILinkchatProps) {
    const {
      env = ENV.BOE,
      debug = false,
      region = Region.cn,
      lang = Lang.zh,
      timeZone,
      envTag,
      apiOrigin,
      chatProps,
      imCloudConfig,
      getSign,
      getUserInfo,
      onNetError,
      metrics,
      useNewAuth,
      linkchatInfo = {
        initWithCreateDialog: true,
        initDialogInfo: {},
      } as ILinkchatInfo,
      ...rest
    } = props || {};
    if (debug) {
      setLoggerLevel(LoggerLevel.info);
    }
    logger.info(Info.Init, props);
    setMonitorEngine(metrics);
    this.getSign = getSign;
    this.getUserInfo = getUserInfo;
    this.onNetError = onNetError;
    this.inNewAuthRole = useNewAuth;
    this.imCloudConfig = imCloudConfig;
    this.metrics = metrics;
    this.envConfig = {
      lang,
      region,
      env,
      debug,
      envTag,
      apiOrigin,
      timeZone,
      ...rest,
    };
    this.appConfig = linkchatInfo;
    this._appContext = new AppContext(this.appConfig?.query);
    this._appContext.ext.dialogEventCode = this.appConfig.eventCode || {};
    this._appContext.dialog = this.appConfig?.initDialogInfo;
    this._appContext.dialogInfo = this.appConfig?.initDialogInfo;
    store.put({ ...linkchatInfo, ...this.envConfig });
    this.runLinkchat(chatProps);
  }

  get appContext() {
    return this._appContext;
  }

  /**
   * @description Get an instance of ChatPro
   * @returns ChatPro
   */
  get core() {
    return this._instance;
  }

  /**
   * @description Get stored data
   * @returns Map < string, any >
   */
  get propsConfig() {
    return {
      envConfig: this.envConfig,
      linkchatInfo: this.appConfig,
      imCloudConfig: this.imCloudConfig,
    };
  }

  get byteImService() {
    const { region = Region.cn } = this.envConfig;
    return (
      this.appContext?.ext?.imCloudConfig ||
      this.imCloudConfig ||
      ByteConfig[region]?.PROD
    );
  }

  get channelInstance() {
    return this._appContext.channel;
  }

  get apis() {
    return {
      ...this.service,
      sendMessage: createSendMessage(this.appContext),
      sendFile: createSendFile(this.appContext),
    };
  }

  get fetchLib() {
    return this.service?.request;
  }

  get chatConfig() {
    return {
      lang: this.envConfig.lang,
      lifecycle: {
        [Lifecycle.CHANNEL_DID_MOUNT]: (
          ctx: IAppContext<CombineMessageType, Channel>,
        ) => {
          ctx.utils.sendMessage = createSendMessage(ctx);
          ctx.utils.sendFile = createSendFile(ctx);
        },
        [Lifecycle.MESSAGES_CHANGE]: (
          ctx: AppContextType,
          data: CombineMessageType[],
        ) => {
          this.messageListChange.next(data);
        },
        [Lifecycle.DIALOG_CHANGE]: (ctx: AppContextType, data: any) => {
          dialogChange(ctx);
          this.dialogChange.next({
            ...data,
            type: data.ext?.type,
            status: data.ext?.status,
            id: data?.dialogId || data?.ext?.dialogId,
          });
        },
        [Lifecycle.APP_CONTEXT_CHANGE]: (ctx: AppContextType) =>
          this.appContextChange.next(ctx),
      },
      createDialog: async (ctx: IAppContext<CombineMessageType>) => {
        const authToken = ctx.userInfo;
        ctx.ext = {
          ...ctx.ext,
          ...authToken,
          ...this.appConfig,
          authed: authToken,
          userName: this.appConfig.userName,
          userId: authToken.imUserId,
        };
        // 当初始化需要创建会话并且 dialogId为空或者等于0的场景需要创建会话

        // 当前是外部传进来的初始值直接赋值，并返回useOfflineForm[是否使用留言表单]
        ctx.watchOfflineForm = this.appConfig.initOfflineForm;
        try {
          if (
            this.appConfig.initWithCreateDialog &&
            (!ctx.dialog?.dialogId || ctx.dialog?.dialogId == 0)
          ) {
            logger.info(Info.CreateDialogLow, {
              ...ctx.dialog,
              initWithCreateDialog: this.appConfig.initWithCreateDialog,
            });
            // 如果走留言表单，则不去请求创建会话
            const {
              type,
              dialog: createDialogResult,
              offlineForm,
            } = await ctx.utils.services?.createDialog({
              ext: this.appConfig.ext,
            });
            if (type === DialogCreateResultType.OFFLINE_FORM) {
              // 如果接口返回为留言表单直接不初始化长链
              await this.appContext.utils?.jumpToOfflineForm(this.appContext, {
                useDmc: true, // 是否流程化
                useOfflineForm: true, // 是否走留言表单
                jarvisId: offlineForm?.jarvisComponentId || '',
              });
              return true;
            }
            if (createDialogResult) {
              const {
                conversationId,
                id,
                conversationShortId,
                conversationTitle,
                createdTime,
                type,
                status,
                ...info
              } = createDialogResult;
              ctx.dialog.id = conversationId;
              ctx.dialog.ext = {
                dialogId: String(id),
                type: Number(type),
                status: Number(status),
                ...info,
              };
            }
            return !!this.appConfig.breakInitChannel;
          } else {
            logger.info(Info.CreateDialogAsync, {
              ...ctx.dialog,
              initWithCreateDialog: this.appConfig.initWithCreateDialog,
              initOfflineForm: this.appConfig.initOfflineForm,
            });
            return !!this.appConfig.breakInitChannel;
          }
        } catch (error) {
          logger.error(Info.CreateDialogError, {
            ...ctx.dialog,
            initWithCreateDialog: this.appConfig.initWithCreateDialog,
            error: error,
          });
          onErrorSubject.next({
            type: ERROR_TYPE.CREATE_DIALOG_ERROR,
            error: error,
          });
          if (!this.envConfig.debug) {
            throw error;
          }
        }
      },
      /** 初始化长链 userId + imc_token */
      makeChannel: async (appContext: AppContextType) => {
        const { filterAgentMessage } = store.getMap();
        const env = this.envConfig.env;
        const { businessLineId, token, imUserId, userId } = appContext.userInfo;
        const imcloudUserId = imUserId || userId;
        logger.info('useWsConfig', {
          ...this.byteImService,
        });
        const initImCloudConfig: ChannelInitProps = {
          pullInterval: 30000,
          timeout: 10000,
          throttle: 1000,
          storage: false,
          retention: 3,
          encryption: true,
          compression: true,
          ...this.byteImService,
          userId: imcloudUserId,
          authType: im_proto.AuthType.TOKEN_AUTH,
          token: token,
          debug: this.envConfig.debug,
          deviceId: `${businessLineId}${imcloudUserId?.slice(-6)}`, //Device ID
          // metrics: this.metrics,
          monitor: [new CustomSlardarWebMonitor(this.metrics)],
          isOversea: this.envConfig.isOversea,
          initLoadSize: this.appConfig.initLoadSize,
        };
        if (this.envConfig.region === Region.sg) {
          initImCloudConfig.vasDomain =
            env === ENV.BOE ? mediaDomain[ENV.BOE] : mediaDomain[ENV.PROD];
          initImCloudConfig.logDomain =
            env === ENV.BOE ? logDomain[ENV.BOE] : logDomain[ENV.PROD];
        }
        if (this.envConfig.envTag && env === ENV.BOE) {
          initImCloudConfig.boe = this.envConfig.envTag;
        }
        if (this.envConfig.envTag && env === ENV.PPE) {
          initImCloudConfig.ppe = this.envConfig.envTag;
        }
        // 0.7.5全量后可以删掉，因为这三个配置的参数在0.5.0版本就已经支持了，传入会有bug。
        if (env === ENV.PPE || env === ENV.PROD) {
          const { region = Region.cn } = this.envConfig;
          let localConfig = ByteConfig[region]?.PROD;
          initImCloudConfig.uploadRegion =
            this.byteImService?.uploadRegion || localConfig?.uploadRegion;
          initImCloudConfig.imageDomain =
            this.byteImService?.imageDomain || localConfig?.imageDomain;
          initImCloudConfig.videoDomain =
            this.byteImService?.videoDomain || localConfig?.videoDomain;
        }
        if (filterAgentMessage) {
          initImCloudConfig.filterAgentMessage = true;
        }
        logger.info(Info.ImcloudConfig, initImCloudConfig);
        return new Channel(initImCloudConfig);
      },
      middleware: {
        receive: [uniqueId, userConfig],
        send: [],
      },
      plugins: [],
      utils: {
        _monitor: monitor,
        services: this.service,
        fetch: (url: string, args: any, pure?: boolean) =>
          pure
            ? this.service?.request(url, args)
            : this.service?.request(url, args).then((res: any) => res.data),
        cancelHeart: () => {
          this.appContext.ext = {
            ...this.appContext.ext,
            userStatus: USER_STATUS.Leave,
          };
          return dialogHeart?.stop();
        },
        startHeart: () => {
          this.appContext.ext = {
            ...this.appContext.ext,
            userStatus: USER_STATUS.Online,
          };
          return dialogHeart?.run(this.appContext);
        },
      },
      logger: logger,
    };
  }

  public dialogStatus() {
    const curStatus = this.appContext.dialog?.ext?.status;
    return curStatus ? Number(curStatus) : null;
  }

  public dialogType() {
    const curType = this.appContext.dialog?.ext?.type;
    return curType ? Number(curType) : null;
  }

  public inAgent() {
    return (
      this.dialogType() === DIALOG_TYPE.AGENT &&
      (this.dialogStatus() === DIALOG_STATUS.PENDING ||
        this.dialogStatus() === DIALOG_STATUS.HOLDON)
    );
  }

  public inOffline() {
    return this.dialogType() === DIALOG_TYPE.OFFLINE;
  }

  public inQueuing() {
    return this.dialogStatus() === DIALOG_STATUS.QUEUEING;
  }

  public inBot() {
    return !this.inAgent() && !this.inQueuing();
  }

  /**
   * @description Pull chat history
   * @returns  {hasMore: boolean, messages: Message [], cursor: string}
   */
  public pullHistoryMessages() {
    return this.appContext.channel?.pullHistoryMessages();
  }

  /**
   * @description 初始化Linkchat core实例
   * @param { chatProps }
   */
  public async runLinkchat(chatProps?: IBaseConfig<any>) {
    let reuqestInstance: AxiosInstance | null = null;
    try {
      if (!this.inNewAuthRole) {
        const userInfo = await this.getUserInfo();
        const tokenInfo = await this.getSign(userInfo);
        reuqestInstance = fetch.create({
          timeout: 10000,
          timeoutErrorMessage: 'request timeout',
          headers: {
            'im-client-mark': 'im_client',
            'im-user-id': tokenInfo.imUserId,
            'im-user-token': tokenInfo.token,
            'secret-str': tokenInfo.secretStr,
            sign: tokenInfo.sign,
            timestamp: tokenInfo.timestamp,
            businessLineId: userInfo.businessLineId,
            lang: store.get('lang'),
          },
        });
        this.appContext.userInfo = {
          ...userInfo,
          ...tokenInfo,
        };
      } else {
        const signData = await this.getUserInfo();
        this.appContext.userInfo = signData;
        if (!signData?.userId) {
          return;
        }
        reuqestInstance = userAuthAxios({
          businessLineId: signData?.businessLineId,
          businessUserId: signData?.userId,
          contactType: EContactType.VISITOR,
          curEnv: this.envConfig.env,
          region: this.envConfig.region || Region.cn,
          getSign: async () => {
            if (!refreshSign) {
              refreshSign = true;
              return signData.sign;
            } else {
              const { sign } = await this.getUserInfo();
              return sign;
            }
          },
          onError: this.onNetError,
        });
        const getToken = getTokenApi(reuqestInstance);
        const { data: tokenInfo } = await getToken(this.envConfig.apiOrigin);
        this.appContext.userInfo = {
          ...this.appContext.userInfo,
          ...tokenInfo,
        };
      }
      this.service = new Http({
        createDialogExt: this.appConfig.ext,
        appContext: this._appContext,
        request: {
          instance: reuqestInstance,
          interceptors: {},
        },
      });
      const mergeConfig = mergeWith(
        this.chatConfig,
        chatProps,
        (objValue, srcValue) => {
          if (isArray(objValue)) {
            return objValue.concat(srcValue);
          }
        },
      );
      this._instance = new Core<CombineMessageType, Channel>(
        mergeConfig,
        this._appContext,
      );
      this._instance.run();
    } catch (error) {
      logger.error('init linkchat error', {
        ...this.envConfig,
      });
      this.appContext.appStatus = Lifecycle.APP_ON_ERROR;
      this.appContext.appLoading = false;
      throw error;
    }
  }

  public destroy() {
    this.channelInstance?.byteIM.event.unsubscribeAll();
  }
}

export default Linkchat;
